import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { OverlayModule, type ConnectedPosition, CdkConnectedOverlay } from '@angular/cdk/overlay';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  addMonths,
  addYears,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  getISOWeek,
  getMonth,
  getYear,
  isSameDay,
  isSameMonth,
  isToday,
  setMonth,
  setYear,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { ZagIcon } from '../icon/icon';
import { TooltipDirective } from '../tooltip/tooltip.directive';
import { ScrollIntoViewDirective } from '../scroll-into-view/scroll-into-view.directive';
import { LanguageService, type SupportedLanguage } from '../services/language.service';
import { FormattingService } from '../formatting/formatting.service';
import { FormField } from '../form-field/form-field';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

type DatePickerView = 'day' | 'month' | 'quarter' | 'year' | 'decade';

const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'] as const;
const WEEKDAY_KEYS_MON_FIRST = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

/** Controls what unit the picker selects: a day, ISO week, month, calendar quarter, or full year. */
export type DatePickerSelectionMode = 'day' | 'month' | 'week' | 'quarter' | 'year';

/** Controls whether the trigger renders as a text input, a compact button, or no trigger at all (inline). */
export type DatePickerTriggerMode = 'input' | 'button' | 'inline';

/** Day of the week the calendar grid starts on (0 = Sunday, 1 = Monday, …, 6 = Saturday). */
export type WeekStartsOn = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** Normalized inclusive date range. Both ends are `YYYY-MM-DD` ISO dates with `start <= end`. */
export interface DatePickerRange {
  readonly start: string;
  readonly end: string;
}

interface WeekRow {
  readonly weekNumber: number;
  readonly weekStart: Date;
  readonly weekStartIso: string;
  readonly isSelected: boolean;
  readonly isCurrent: boolean;
  readonly isRangeStart: boolean;
  readonly isInRange: boolean;
  readonly isRangeEnd: boolean;
  readonly days: ReadonlyArray<{
    readonly date: Date;
    readonly dayOfMonth: number;
    readonly isCurrentMonth: boolean;
    readonly isToday: boolean;
  }>;
}

interface DayCell {
  readonly date: Date;
  readonly iso: string;
  readonly dayOfMonth: number;
  readonly isCurrentMonth: boolean;
  readonly isToday: boolean;
  readonly isSelected: boolean;
  readonly isDisabled: boolean;
  readonly isRangeStart: boolean;
  readonly isInRange: boolean;
  readonly isRangeEnd: boolean;
}

interface OptionCell {
  readonly label: string;
  readonly value: number;
  readonly iso: string;
  readonly isSelected: boolean;
  readonly isCurrent: boolean;
  readonly isDisabled: boolean;
  readonly isOutsideRange?: boolean;
  readonly isRangeStart: boolean;
  readonly isInRange: boolean;
  readonly isRangeEnd: boolean;
}

type DisabledDateMatcher = (isoDate: string, date: Date) => boolean;

/** Parse a user-typed date string. Returns null on failure.
 *
 * Supported formats:
 * - `dd.MM.yyyy` — European dot-separated (e.g. 15.05.2025)
 * - `dd/MM/yyyy` — slash-separated (e.g. 15/05/2025)
 * - `yyyy-MM-dd` — ISO (e.g. 2025-05-15)
 * - `DDMMYYYY` — compact 8-digit (e.g. 15052025 → 2025-05-15)
 */
export function parseTypedDate(input: string): Date | null {
  const trimmed = input.trim();

  // Try dd.MM.yyyy
  const ddmmDotMatch = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(trimmed);
  if (ddmmDotMatch) {
    return buildValidDate(Number(ddmmDotMatch[3]), Number(ddmmDotMatch[2]), Number(ddmmDotMatch[1]));
  }

  // Try dd/MM/yyyy
  const ddmmSlashMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (ddmmSlashMatch) {
    return buildValidDate(Number(ddmmSlashMatch[3]), Number(ddmmSlashMatch[2]), Number(ddmmSlashMatch[1]));
  }

  // Try yyyy-MM-dd
  const isoMatch = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(trimmed);
  if (isoMatch) {
    return buildValidDate(Number(isoMatch[1]), Number(isoMatch[2]), Number(isoMatch[3]));
  }

  // Try compact DDMMYYYY (exactly 8 digits, no separators)
  const compactMatch = /^(\d{2})(\d{2})(\d{4})$/.exec(trimmed);
  if (compactMatch) {
    return buildValidDate(Number(compactMatch[3]), Number(compactMatch[2]), Number(compactMatch[1]));
  }

  return null;
}

function buildValidDate(year: number, month: number, day: number): Date | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(year, month - 1, day);
  if (getYear(date) !== year || getMonth(date) !== month - 1 || date.getDate() !== day) return null;
  return date;
}

/** Return the date format separator character for the given language. */
function dateSeparatorForLang(lang: SupportedLanguage): string {
  return lang === 'en' ? '/' : '.';
}

/** Return the mask template for the given language (e.g. "DD.MM.YYYY" or "DD/MM/YYYY"). */
function dateMaskForLang(lang: SupportedLanguage): string {
  const sep = dateSeparatorForLang(lang);
  return `DD${sep}MM${sep}YYYY`;
}

/** Apply a date mask to raw digit input.
 *  Accepts the current raw value of the input (digits + separators as typed so far)
 *  and returns the new masked string, inserting or removing the separator as needed.
 */
export function applyDateMask(raw: string, sep: string): string {
  // Strip everything except digits
  const digits = raw.replaceAll(/\D/g, '').slice(0, 8);
  let result = '';
  for (let i = 0; i < digits.length; i++) {
    if (i === 2 || i === 4) result += sep;
    result += digits[i];
  }
  return result;
}

@Component({
  selector: 'zag-date-picker',
  imports: [OverlayModule, ZagIcon, TooltipDirective, ScrollIntoViewDirective, TranslatePipe, NgTemplateOutlet],
  templateUrl: './date-picker.html',
  styleUrl: './date-picker.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePicker),
      multi: true,
    },
  ],
  host: {
    class: 'zag-date-picker',
    '(document:keydown.escape)': 'onEscape()',
  },
})
export class DatePicker implements ControlValueAccessor, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly hostRef = inject(ElementRef<HTMLElement>);
  private readonly languageService = inject(LanguageService);
  private readonly formattingService = inject(FormattingService);
  private readonly translateService = inject(TranslateService);
  private readonly document = inject(DOCUMENT);
  private readonly formField = inject(FormField, { optional: true });

  private readonly connectedOverlay = viewChild(CdkConnectedOverlay);

  private rafId?: number;
  private lastOverlayWidth?: number;
  private readonly mobileQuery =
    typeof this.document.defaultView?.matchMedia === 'function' ? this.document.defaultView.matchMedia('(max-width: 539px)') : null;
  protected readonly isMobile = signal<boolean>(this.mobileQuery?.matches ?? false);
  private readonly onMobileChange = (e: MediaQueryListEvent) => {
    this.isMobile.set(e.matches);
    this.cdr.markForCheck();
  };

  readonly placeholder = input<string>('Select date');
  readonly disabled = input<boolean>(false);
  readonly readonly = input<boolean>(false);
  readonly id = input<string | undefined>(undefined);
  readonly name = input<string | undefined>(undefined);
  readonly required = input<boolean>(false);
  readonly clearable = input<boolean>(true);
  readonly min = input<string | undefined>(undefined);
  readonly max = input<string | undefined>(undefined);
  readonly disabledDates = input<readonly string[]>([]);
  readonly disabledDateMatcher = input<DisabledDateMatcher | undefined>(undefined);
  /** When true, the text input shows a locale-aware date format mask (e.g. DD.MM.YYYY) */
  readonly maskInput = input<boolean>(false);
  readonly value = model<string>('');
  /**
   * Controls what unit is selected.
   * - 'day'   — standard single-day picker (default)
   * - 'month' — opens at month zoom; clicking a month cell commits and closes
   * - 'week'  — day view shows whole-week rows; clicking a row commits the week start and closes
   */
  readonly selectionMode = input<DatePickerSelectionMode>('day');
  /**
   * Controls the trigger element.
   * - 'input'  — standard text input trigger (default)
   * - 'button' — compact button trigger showing the selected period label
   */
  readonly triggerMode = input<DatePickerTriggerMode>('input');
  /** Optional leading icon for the button trigger (e.g. a calendar glyph). */
  readonly triggerLeadingIcon = input<string>('');
  /** Render the button trigger with the input field background and border. */
  readonly triggerField = input<boolean>(false);
  /**
   * The day of the week the calendar grid starts on.
   * 0 = Sunday, 1 = Monday (default), 2 = Tuesday, …, 6 = Saturday.
   * Can be changed from the outside to respect per-user settings.
   */
  readonly weekStartsOn = input<WeekStartsOn>(1);
  /**
   * When true and selectionMode='day', the button label shows the full locale-aware date
   * (weekday long + day + month long + year) instead of the compact dd.MM.yyyy format.
   */
  readonly fullDayLabel = input<boolean>(false);
  /** Uses shortened button-trigger labels on mobile viewports. */
  readonly compactLabel = input<boolean>(false);
  /** Emitted when a whole week is selected (selectionMode='week'). Carries the ISO week-start date string. */
  readonly weekSelected = output<string>();
  // Shows the ISO week-number gutter in day view without changing day selection.
  readonly showWeekNumbers = input<boolean>(false);
  /** When true, shift-click extends from an anchor; plain click resets anchor + single-point range. */
  readonly rangeSelection = input<boolean>(false);
  /** Normalized inclusive range (`start <= end`, `YYYY-MM-DD`). Two-way: `(rangeChange)` emits on every anchor/extend/clear. */
  readonly range = model<DatePickerRange | null>(null);

  /** Returns a translator function; re-evaluates when language changes, making all dependents reactive. */
  private readonly translator = computed(() => {
    this.languageService.currentLanguage();
    return (key: string) => this.translateService.instant(key) as string;
  });

  protected readonly maskSeparator = computed(() => dateSeparatorForLang(this.languageService.currentLanguage()));
  protected readonly maskPlaceholder = computed(() => dateMaskForLang(this.languageService.currentLanguage()));

  protected readonly open = signal<boolean>(false);
  protected readonly viewMode = signal<DatePickerView>('day');
  protected readonly cursorDate = signal<Date>(new Date());
  protected readonly inputText = signal<string>('');
  protected readonly inputError = signal<boolean>(false);

  /** True when the picker renders the calendar panel inline (no trigger, no overlay). */
  protected readonly isInline = computed(() => this.triggerMode() === 'inline');
  /** True when the calendar panel should be visible (always true in inline mode, otherwise driven by `open`). */
  protected readonly isPanelVisible = computed(() => this.isInline() || this.open());

  /** Range driving highlight: pending staging on mobile overlay, committed range otherwise. */
  protected readonly effectiveRange = computed<DatePickerRange | null>(() => {
    if (!this.rangeSelection()) return null;
    if (this.isMobile() && !this.isInline()) return this.pendingRange() ?? this.range();
    return this.range();
  });

  protected readonly rangeStartDate = computed<Date | null>(() => this.parseIsoDate(this.effectiveRange()?.start));
  protected readonly rangeEndDate = computed<Date | null>(() => this.parseIsoDate(this.effectiveRange()?.end));

  /** True when the current selection already covers today — the inline today button hides then. */
  protected readonly isTodaySelected = computed(() => {
    if (this.rangeSelection()) {
      const start = this.rangeStartDate();
      const end = this.rangeEndDate();
      if (!start || !end) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const lo = new Date(start);
      lo.setHours(0, 0, 0, 0);
      const hi = new Date(end);
      hi.setHours(23, 59, 59, 999);
      return today >= lo && today <= hi;
    }
    const selected = this.selectedDate();
    if (!selected) return false;
    const today = new Date();
    switch (this.selectionMode()) {
      case 'week':
        return isSameDay(selected, startOfWeek(today, { weekStartsOn: this.weekStartsOn() }));
      case 'month':
        return isSameMonth(selected, today);
      case 'quarter':
        return Math.floor(getMonth(selected) / 3) === Math.floor(getMonth(today) / 3) && getYear(selected) === getYear(today);
      case 'year':
        return getYear(selected) === getYear(today);
      default:
        return isSameDay(selected, today);
    }
  });

  /** Label auto-detected from parent FormField for mobile overlay header */
  protected readonly fieldLabel = computed(() => this.formField?.label() ?? '');

  /** Pending date for mobile mode — selection is staged until Apply is pressed */
  protected readonly pendingDate = signal<Date | null>(null);
  /** When the pending selection originates from a week row, store the ISO week-start string so applyMobile emits weekSelected */
  private readonly pendingWeekIso = signal<string | null>(null);
  /** Anchor date for shift-extend. Set on plain click, kept across shift-clicks until the next plain-click reset. */
  private readonly rangeAnchor = signal<Date | null>(null);
  /** Mobile staging for range mode — mirrors rangeAnchor/range until Apply is pressed. */
  private readonly pendingAnchor = signal<Date | null>(null);
  protected readonly pendingRange = signal<DatePickerRange | null>(null);

  protected readonly overlayPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 4,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -4,
    },
  ];

  /** On mobile the overlay is centred in the viewport via the panel class. */
  protected readonly mobileOverlayPositions: ConnectedPosition[] = [
    { originX: 'center', originY: 'center', overlayX: 'center', overlayY: 'center' },
  ];

  protected readonly selectedDate = computed<Date | null>(() => this.parseIsoDate(this.value()));

  protected readonly minDate = computed<Date | null>(() => this.parseIsoDate(this.min()));

  protected readonly maxDate = computed<Date | null>(() => this.parseIsoDate(this.max()));

  protected readonly disabledDateSet = computed<Set<string>>(() => {
    const set = new Set<string>();
    for (const v of this.disabledDates()) {
      const parsed = this.parseIsoDate(v);
      if (parsed !== null) set.add(this.toIsoDate(parsed));
    }
    return set;
  });

  protected readonly showClearButton = computed(
    () =>
      this.clearable() &&
      (this.selectedDate() !== null || this.effectiveRange() !== null || this.inputText().trim().length > 0) &&
      !this.disabled() &&
      !this.readonly()
  );

  /** Whether the zoom-out title button should be disabled for the current mode. */
  protected readonly isZoomOutDisabled = computed(() => {
    const view = this.viewMode();
    const mode = this.selectionMode();
    if (view === 'decade') return true;
    if (view === 'quarter') return true;
    if (view === 'year' && mode === 'year') return true;
    return false;
  });

  protected readonly headerLabel = computed(() => {
    const t = this.translator();
    const date = this.cursorDate();
    const mode = this.viewMode();
    if (mode === 'day') {
      const monthName = t(`breadcrumb.monthsFull.${MONTH_KEYS[getMonth(date)]}`);
      return `${monthName} ${getYear(date)}`;
    }
    if (mode === 'month' || mode === 'quarter') return String(getYear(date));
    if (mode === 'year') {
      const start = this.getYearGridStart(getYear(date)) + 1;
      return `${start}-${start + 9}`;
    }
    const start = this.getDecadeGridStart(getYear(date)) + 10;
    return `${start}-${start + 99}`;
  });

  /** Weekday header labels (i18n, Mon-first order) rotated so the first column matches weekStartsOn. */
  protected readonly weekdayLabels = computed(() => {
    const t = this.translator();
    const labels = WEEKDAY_KEYS_MON_FIRST.map(k => t(`breadcrumb.daysShort.${k}`));
    const start = this.weekStartsOn();
    const offset = start === 0 ? 6 : start - 1;
    return [...labels.slice(offset), ...labels.slice(0, offset)];
  });

  protected readonly dayCells = computed((): DayCell[] => {
    const cursor = this.cursorDate();
    // On mobile, highlight the pending date; on desktop, highlight the committed selected date
    const selected = this.isMobile() ? (this.pendingDate() ?? this.selectedDate()) : this.selectedDate();
    const rangeStart = this.rangeStartDate();
    const rangeEnd = this.rangeEndDate();
    const wso = this.weekStartsOn();
    const monthStart = startOfMonth(cursor);
    const monthEnd = endOfMonth(cursor);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: wso });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: wso });

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd }).map(day => {
      const isRangeStart = rangeStart ? isSameDay(day, rangeStart) : false;
      const isRangeEnd = rangeEnd ? isSameDay(day, rangeEnd) : false;
      const isInRange = rangeStart && rangeEnd ? day > rangeStart && day < rangeEnd : false;
      const singleSelected = selected ? isSameDay(day, selected) : false;
      const isSelected = this.rangeSelection() ? isRangeStart || isRangeEnd : singleSelected;
      return {
        date: day,
        iso: this.toIsoDate(day),
        dayOfMonth: day.getDate(),
        isCurrentMonth: isSameMonth(day, cursor),
        isToday: isToday(day),
        isSelected,
        isDisabled: !this.isDateAllowed(day),
        isRangeStart,
        isInRange,
        isRangeEnd,
      };
    });
  });

  protected readonly monthCells = computed((): OptionCell[] => {
    const t = this.translator();
    const selected = this.isMobile() ? (this.pendingDate() ?? this.selectedDate()) : this.selectedDate();
    const rangeStart = this.rangeStartDate();
    const rangeEnd = this.rangeEndDate();
    const currentDate = new Date();
    const year = getYear(this.cursorDate());

    return MONTH_KEYS.map((key, month) => {
      const start = startOfMonth(new Date(year, month, 1));
      const end = endOfMonth(start);
      const key24 = year * 12 + month;
      const lo = rangeStart ? getYear(rangeStart) * 12 + getMonth(rangeStart) : null;
      const hi = rangeEnd ? getYear(rangeEnd) * 12 + getMonth(rangeEnd) : null;
      const isRangeStart = lo !== null && key24 === lo;
      const isRangeEnd = hi !== null && key24 === hi;
      const isInRange = lo !== null && hi !== null && key24 > lo && key24 < hi;
      const singleSelected = selected !== null && getYear(selected) === year && getMonth(selected) === month;
      return {
        label: t(`breadcrumb.months.${key}`),
        value: month,
        iso: this.toIsoDate(start),
        isSelected: this.rangeSelection() ? isRangeStart || isRangeEnd : singleSelected,
        isCurrent: getYear(currentDate) === year && getMonth(currentDate) === month,
        isDisabled: !this.isRangeSelectable(start, end),
        isRangeStart,
        isInRange,
        isRangeEnd,
      };
    });
  });

  protected readonly quarterCells = computed((): OptionCell[] => {
    const selected = this.isMobile() ? (this.pendingDate() ?? this.selectedDate()) : this.selectedDate();
    const rangeStart = this.rangeStartDate();
    const rangeEnd = this.rangeEndDate();
    const currentDate = new Date();
    const year = getYear(this.cursorDate());

    return [0, 1, 2, 3].map(q => {
      const startMonth = q * 3;
      const start = startOfMonth(new Date(year, startMonth, 1));
      const end = endOfMonth(new Date(year, startMonth + 2, 1));
      const key = year * 4 + q;
      const lo = rangeStart ? getYear(rangeStart) * 4 + Math.floor(getMonth(rangeStart) / 3) : null;
      const hi = rangeEnd ? getYear(rangeEnd) * 4 + Math.floor(getMonth(rangeEnd) / 3) : null;
      const isRangeStart = lo !== null && key === lo;
      const isRangeEnd = hi !== null && key === hi;
      const isInRange = lo !== null && hi !== null && key > lo && key < hi;
      const singleSelected = selected !== null && getYear(selected) === year && Math.floor(getMonth(selected) / 3) === q;
      return {
        label: `Q${q + 1}`,
        value: q,
        iso: this.toIsoDate(start),
        isSelected: this.rangeSelection() ? isRangeStart || isRangeEnd : singleSelected,
        isCurrent: getYear(currentDate) === year && Math.floor(getMonth(currentDate) / 3) === q,
        isDisabled: !this.isRangeSelectable(start, end),
        isRangeStart,
        isInRange,
        isRangeEnd,
      };
    });
  });

  protected readonly yearCells = computed((): OptionCell[] => {
    const selected = this.selectedDate();
    const rangeStartDate = this.rangeStartDate();
    const rangeEndDate = this.rangeEndDate();
    const currentYear = getYear(new Date());
    const start = this.getYearGridStart(getYear(this.cursorDate()));

    return Array.from({ length: 12 }, (_, index) => {
      const year = start + index;
      const rangeStart = new Date(year, 0, 1);
      const rangeEnd = new Date(year, 11, 31);
      const lo = rangeStartDate ? getYear(rangeStartDate) : null;
      const hi = rangeEndDate ? getYear(rangeEndDate) : null;
      const isRangeStart = lo !== null && year === lo;
      const isRangeEnd = hi !== null && year === hi;
      const isInRange = lo !== null && hi !== null && year > lo && year < hi;
      return {
        label: String(year),
        value: year,
        iso: this.toIsoDate(rangeStart),
        isSelected: this.rangeSelection() ? isRangeStart || isRangeEnd : selected !== null && getYear(selected) === year,
        isCurrent: currentYear === year,
        isDisabled: !this.isRangeSelectable(rangeStart, rangeEnd),
        isOutsideRange: index === 0 || index === 11,
        isRangeStart,
        isInRange,
        isRangeEnd,
      };
    });
  });

  protected readonly decadeCells = computed((): OptionCell[] => {
    const selected = this.selectedDate();
    const currentYear = getYear(new Date());
    const start = this.getDecadeGridStart(getYear(this.cursorDate()));

    return Array.from({ length: 12 }, (_, index) => {
      const decadeStart = start + index * 10;
      const decadeEnd = decadeStart + 9;
      const rangeStart = new Date(decadeStart, 0, 1);
      const rangeEnd = new Date(decadeEnd, 11, 31);
      return {
        label: `${decadeStart}-${decadeEnd}`,
        value: decadeStart,
        iso: this.toIsoDate(rangeStart),
        isSelected: selected !== null && getYear(selected) >= decadeStart && getYear(selected) <= decadeEnd,
        isCurrent: currentYear >= decadeStart && currentYear <= decadeEnd,
        isDisabled: !this.isRangeSelectable(rangeStart, rangeEnd),
        isOutsideRange: index === 0 || index === 11,
        isRangeStart: false,
        isInRange: false,
        isRangeEnd: false,
      };
    });
  });

  /** ISO weekday labels for week-mode — delegates to weekdayLabels (same data, reactive to language). */
  protected readonly i18nWeekdayLabels = this.weekdayLabels;

  // True for the day-view gutter variant: week numbers shown, day cells still select days.
  protected readonly showDayWeekNumbers = computed(() => this.showWeekNumbers() && this.selectionMode() !== 'week');

  // Day cells chunked into 7-day rows with the leading ISO week number for the gutter variant.
  protected readonly dayWeeks = computed((): ReadonlyArray<{ weekNumber: number; weekStartIso: string; cells: DayCell[] }> => {
    const cells = this.dayCells();
    const weeks: Array<{ weekNumber: number; weekStartIso: string; cells: DayCell[] }> = [];
    for (let i = 0; i < cells.length; i += 7) {
      const chunk = cells.slice(i, i + 7);
      if (chunk.length === 0) continue;
      weeks.push({ weekNumber: getISOWeek(chunk[0].date), weekStartIso: chunk[0].iso, cells: chunk });
    }
    return weeks;
  });

  /** Week rows for the 'week' selectionMode day view. */
  protected readonly weekRows = computed((): WeekRow[] => {
    const cursor = this.cursorDate();
    const pendingWeek = this.pendingWeekIso();
    const selectedIso = this.isMobile() && pendingWeek ? pendingWeek : this.value();
    const rangeStart = this.rangeStartDate();
    const rangeEnd = this.rangeEndDate();
    const now = new Date();
    const wso = this.weekStartsOn();
    const monthStart = startOfMonth(cursor);
    const monthEnd = endOfMonth(cursor);
    const calStart = startOfWeek(monthStart, { weekStartsOn: wso });
    const calEnd = endOfWeek(monthEnd, { weekStartsOn: wso });

    // Collect all weeks covering the month
    const rows: WeekRow[] = [];
    let weekStart = calStart;
    while (weekStart <= calEnd) {
      const weekEnd = endOfWeek(weekStart, { weekStartsOn: wso });
      const weekStartIso = this.toIsoDate(weekStart);
      const currentWeekStart = startOfWeek(now, { weekStartsOn: wso });
      const containsStart = rangeStart ? rangeStart >= weekStart && rangeStart <= weekEnd : false;
      const containsEnd = rangeEnd ? rangeEnd >= weekStart && rangeEnd <= weekEnd : false;
      const isRangeStart = containsStart;
      const isRangeEnd = containsEnd;
      const isInRange = rangeStart && rangeEnd ? weekStart > rangeStart && weekEnd < rangeEnd : false;
      rows.push({
        weekNumber: getISOWeek(weekStart),
        weekStart,
        weekStartIso,
        isSelected: this.rangeSelection() ? isRangeStart || isRangeEnd : selectedIso === weekStartIso,
        isCurrent: weekStart.getTime() === currentWeekStart.getTime(),
        isRangeStart,
        isInRange,
        isRangeEnd,
        days: eachDayOfInterval({ start: weekStart, end: weekEnd }).map(d => ({
          date: d,
          dayOfMonth: d.getDate(),
          isCurrentMonth: isSameMonth(d, cursor),
          isToday: isToday(d),
        })),
      });
      weekStart = new Date(weekEnd.getTime() + 24 * 60 * 60 * 1000);
    }
    return rows;
  });

  /** Label shown in the button trigger. */
  protected readonly buttonLabel = computed(() => {
    const mode = this.selectionMode();
    const compact = this.compactLabel() && this.isMobile();
    if (this.rangeSelection()) {
      const start = this.rangeStartDate();
      const end = this.rangeEndDate();
      if (start && end && this.toIsoDate(start) !== this.toIsoDate(end) && !this.isSameSelectionPeriod(start, end, mode)) {
        return `${this.formatSingleButtonLabel(start, mode, compact)} – ${this.formatSingleButtonLabel(end, mode, compact)}`;
      }
    }
    const iso = this.value();
    if (!iso) return '';
    const date = this.parseIsoDate(iso);
    if (!date) return '';
    return this.formatSingleButtonLabel(date, mode, compact);
  });

  /** True when two dates fall in the same selection unit (one week / month / quarter / year). */
  private isSameSelectionPeriod(a: Date, b: Date, mode: DatePickerSelectionMode): boolean {
    if (mode === 'week')
      return isSameDay(startOfWeek(a, { weekStartsOn: this.weekStartsOn() }), startOfWeek(b, { weekStartsOn: this.weekStartsOn() }));
    if (mode === 'month') return isSameMonth(a, b);
    if (mode === 'quarter') return getYear(a) === getYear(b) && Math.floor(getMonth(a) / 3) === Math.floor(getMonth(b) / 3);
    if (mode === 'year') return getYear(a) === getYear(b);
    return this.toIsoDate(a) === this.toIsoDate(b);
  }

  private formatSingleButtonLabel(date: Date, mode: DatePickerSelectionMode, compact: boolean): string {
    if (mode === 'week') return this.formatWeekButtonLabel(date, compact);
    if (mode === 'month') {
      const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
      const key = monthKeys[getMonth(date)];
      const monthName = this.translateService.instant(`breadcrumb.monthsFull.${key}`);
      return `${monthName} ${getYear(date)}`;
    }
    if (mode === 'quarter') {
      const quarter = Math.floor(getMonth(date) / 3) + 1;
      return `Q${quarter} ${getYear(date)}`;
    }
    if (mode === 'year') {
      return String(getYear(date));
    }
    if (compact) return format(date, 'dd.MM.yy');
    if (this.fullDayLabel()) {
      const calendarDate = new Date(Date.UTC(getYear(date), getMonth(date), date.getDate()));
      return this.formattingService.formatCustom(
        calendarDate,
        {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        },
        { timeZone: 'UTC' }
      );
    }
    return format(date, 'dd.MM.yyyy');
  }

  private formatWeekButtonLabel(date: Date, compact: boolean): string {
    const weekNum = getISOWeek(date);
    const kwLabel = this.translateService.instant('calendar.week');
    if (compact) return `${kwLabel} ${weekNum}`;
    const year = getYear(date);
    const weekStart = startOfWeek(date, { weekStartsOn: this.weekStartsOn() });
    const weekEnd = endOfWeek(date, { weekStartsOn: this.weekStartsOn() });
    const startMonth = this.translateService.instant(`breadcrumb.monthsFull.${MONTH_KEYS[getMonth(weekStart)]}`);
    if (isSameMonth(weekStart, weekEnd)) return `${kwLabel} ${weekNum} · ${startMonth} ${year}`;
    const endMonth = this.translateService.instant(`breadcrumb.monthsFull.${MONTH_KEYS[getMonth(weekEnd)]}`);
    const endYear = getYear(weekEnd);
    if (year === endYear) return `${kwLabel} ${weekNum} · ${startMonth}/${endMonth} ${year}`;
    return `${kwLabel} ${weekNum} · ${startMonth} ${year}/${endMonth} ${endYear}`;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: string) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  constructor() {
    this.mobileQuery?.addEventListener('change', this.onMobileChange);

    effect(() => {
      if (this.disabled() && !this.isInline()) {
        this.open.set(false);
        this.stopPositionMonitoring();
        this.cdr.markForCheck();
      }
    });

    effect(() => {
      if (this.open() && !this.isMobile() && !this.isInline()) {
        this.startPositionMonitoring();
      } else {
        this.stopPositionMonitoring();
      }
    });

    // Inline mode: initialize / reset the view + cursor whenever the picker
    // becomes inline or the selectionMode changes underneath.
    effect(() => {
      if (!this.isInline()) return;
      this.selectionMode(); // tracked dependency
      this.viewMode.set(this.initialViewForSelectionMode());
      const selected = this.rangeSelection() ? (this.rangeStartDate() ?? this.selectedDate()) : this.selectedDate();
      if (selected) {
        this.cursorDate.set(selected);
      }
    });

    // Standalone `[value]` binding (no ControlValueAccessor): mirror the
    // model into the visible input text — writeValue() only covers the
    // reactive-forms path. No-op while typing: a committed parse produces
    // the identical formatted text.
    effect(() => {
      if (this.rangeSelection()) return;
      const parsed = this.parseIsoDate(this.value());
      this.maskSeparator(); // re-run when language changes so the separator in the input updates live
      untracked(() => {
        if (!parsed) return;
        const formatted = this.formatDateForInput(parsed);
        if (this.inputText() !== formatted) {
          this.inputText.set(formatted);
          this.cursorDate.set(parsed);
        }
      });
    });

    // Standalone `[range]` binding: mirror the range model into the input text
    // + cursor, and adopt the range start as anchor so shift-click extends it.
    effect(() => {
      if (!this.rangeSelection()) return;
      const current = this.range();
      this.maskSeparator();
      untracked(() => {
        if (!current) return;
        const start = this.parseIsoDate(current.start);
        const end = this.parseIsoDate(current.end);
        if (!start || !end) return;
        if (!this.rangeAnchor()) this.rangeAnchor.set(start);
        const formatted = this.formatRangeForInput(current);
        if (this.inputText() !== formatted) {
          this.inputText.set(formatted);
          this.cursorDate.set(start);
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.stopPositionMonitoring();
    this.mobileQuery?.removeEventListener('change', this.onMobileChange);
  }

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
    const parsedDate = this.parseIsoDate(value ?? '');
    if (parsedDate) {
      this.cursorDate.set(parsedDate);
      if (this.rangeSelection()) {
        this.rangeAnchor.set(parsedDate);
        const single: DatePickerRange = { start: this.toIsoDate(parsedDate), end: this.toIsoDate(parsedDate) };
        this.range.set(single);
        this.inputText.set(this.formatRangeForInput(single));
      } else {
        this.inputText.set(this.formatDateForInput(parsedDate));
      }
    } else {
      this.inputText.set('');
      if (this.rangeSelection()) {
        this.rangeAnchor.set(null);
        this.range.set(null);
      }
    }
    this.inputError.set(false);
    this.cdr.markForCheck();
  }

  /** Format a date for display in the input field, respecting the current locale separator. */
  private formatDateForInput(date: Date): string {
    const sep = this.maskSeparator();
    const d = String(date.getDate()).padStart(2, '0');
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const y = String(date.getFullYear());
    return `${d}${sep}${m}${sep}${y}`;
  }

  /** Format a range for the input field: single date when collapsed, `start – end` otherwise. */
  private formatRangeForInput(current: DatePickerRange | null): string {
    if (!current) return '';
    const start = this.parseIsoDate(current.start);
    const end = this.parseIsoDate(current.end);
    if (!start || !end) return '';
    if (current.start === current.end) return this.formatDateForInput(start);
    return `${this.formatDateForInput(start)} – ${this.formatDateForInput(end)}`;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(_isDisabled: boolean): void {
    this.cdr.markForCheck();
  }

  protected onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (this.maskInput()) {
      const sep = this.maskSeparator();
      const masked = applyDateMask(target.value, sep);
      this.inputText.set(masked);
      // Update DOM value so cursor position is correct after masking
      target.value = masked;
    } else {
      this.inputText.set(target.value);
    }
    this.inputError.set(false);
    // Live-update the calendar when the typed text is already a valid date
    const parsed = parseTypedDate(this.inputText().trim());
    if (parsed && this.isDateAllowed(parsed)) {
      this.cursorDate.set(parsed);
    }
  }

  protected onInputBlur(event: FocusEvent): void {
    this.onTouched();
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    if (this.open() && relatedTarget?.closest('.zag-date-picker-panel')) return;
    this.commitTypedInput();
  }

  protected onInputFocus(): void {
    this.openPanel();
    const el = this.document.activeElement as HTMLInputElement | null;
    if (el) {
      setTimeout(() => el.select(), 0);
    }
  }

  protected onInputKeyDown(event: KeyboardEvent): void {
    if (this.maskInput()) {
      this.handleMaskedKeyDown(event);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      this.commitTypedInput();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      if (this.open()) {
        this.close();
      }
    } else if (event.key === 'ArrowDown' || event.key === 'F4') {
      event.preventDefault();
      if (!this.open()) this.openPanel();
    }
  }

  /** Handle keydown in mask mode: allow only digit keys, Backspace, Delete, Enter, Escape, Tab, Arrow keys. */
  private handleMaskedKeyDown(event: KeyboardEvent): void {
    const allowed = [
      'Backspace',
      'Delete',
      'Enter',
      'Escape',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'ArrowDown',
      'ArrowUp',
      'F4',
      'Home',
      'End',
    ];
    if (allowed.includes(event.key)) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.commitTypedInput();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        if (this.open()) this.close();
      } else if (event.key === 'ArrowDown' || event.key === 'F4') {
        event.preventDefault();
        if (!this.open()) this.openPanel();
      }
      return;
    }
    // Allow digits only
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  protected openPanel(): void {
    if (this.disabled() || this.readonly() || this.open()) return;
    this.viewMode.set(this.initialViewForSelectionMode());
    this.cursorDate.set(this.selectedDate() ?? this.rangeStartDate() ?? new Date());
    this.pendingDate.set(this.selectedDate());
    this.pendingWeekIso.set(null);
    if (this.rangeSelection()) {
      if (!this.rangeAnchor()) {
        const adopted = this.rangeStartDate() ?? this.selectedDate();
        if (adopted) this.rangeAnchor.set(adopted);
      }
      this.pendingAnchor.set(this.rangeAnchor());
      this.pendingRange.set(this.range());
    }
    this.open.set(true);
  }

  /** Root view for a given selection mode — also the zoom-out floor for that mode. */
  private initialViewForSelectionMode(): DatePickerView {
    switch (this.selectionMode()) {
      case 'month':
        return 'month';
      case 'quarter':
        return 'quarter';
      case 'year':
        return 'year';
      default:
        return 'day';
    }
  }

  protected close(): void {
    this.open.set(false);
    this.pendingDate.set(null);
    this.pendingWeekIso.set(null);
    this.pendingAnchor.set(null);
    this.pendingRange.set(null);
    this.onTouched();
  }

  protected onEscape(): void {
    if (this.isInline()) return;
    if (this.open()) this.close();
  }

  protected clear(event: Event): void {
    event.stopPropagation();
    this.value.set('');
    this.onChange('');
    this.rangeAnchor.set(null);
    this.range.set(null);
    this.pendingAnchor.set(null);
    this.pendingRange.set(null);
    this.inputText.set('');
    this.inputError.set(false);
    this.open.set(false);
    this.onTouched();
  }

  protected navigatePrevious(): void {
    const cursor = this.cursorDate();
    const mode = this.viewMode();
    if (mode === 'day') {
      this.cursorDate.set(addMonths(cursor, -1));
      return;
    }
    if (mode === 'month' || mode === 'quarter') {
      this.cursorDate.set(addYears(cursor, -1));
      return;
    }
    if (mode === 'year') {
      this.cursorDate.set(addYears(cursor, -10));
      return;
    }
    this.cursorDate.set(addYears(cursor, -100));
  }

  protected navigateNext(): void {
    const cursor = this.cursorDate();
    const mode = this.viewMode();
    if (mode === 'day') {
      this.cursorDate.set(addMonths(cursor, 1));
      return;
    }
    if (mode === 'month' || mode === 'quarter') {
      this.cursorDate.set(addYears(cursor, 1));
      return;
    }
    if (mode === 'year') {
      this.cursorDate.set(addYears(cursor, 10));
      return;
    }
    this.cursorDate.set(addYears(cursor, 100));
  }

  /** Inline today button: jumps the selection to the period containing today. */
  protected goToday(): void {
    const today = new Date();
    const mode = this.selectionMode();
    if (this.rangeSelection()) {
      this.handleRangeSelect(this.normalizeToUnit(today, mode), false, mode);
      return;
    }
    if (this.isMobile() && !this.isInline()) {
      const staged = this.normalizeToUnit(today, mode);
      this.pendingDate.set(staged);
      this.pendingWeekIso.set(mode === 'week' ? this.toIsoDate(staged) : null);
      return;
    }
    if (mode === 'week') {
      const weekStart = startOfWeek(today, { weekStartsOn: this.weekStartsOn() });
      const iso = this.toIsoDate(weekStart);
      this.value.set(iso);
      this.onChange(iso);
      this.weekSelected.emit(iso);
      this.cursorDate.set(weekStart);
      return;
    }
    if (mode === 'month') {
      this.commitDate(startOfMonth(today));
      return;
    }
    if (mode === 'quarter') {
      this.commitDate(startOfMonth(new Date(getYear(today), Math.floor(getMonth(today) / 3) * 3, 1)));
      return;
    }
    if (mode === 'year') {
      this.commitDate(new Date(getYear(today), 0, 1));
      return;
    }
    this.commitDate(today);
  }

  protected zoomOut(): void {
    const mode = this.viewMode();
    if (mode === 'day') {
      this.viewMode.set('month');
      return;
    }
    if (mode === 'month') {
      this.viewMode.set('year');
      return;
    }
    if (mode === 'year') {
      this.viewMode.set('decade');
    }
  }

  protected selectDay(day: DayCell, event?: MouseEvent | PointerEvent): void {
    if (day.isDisabled) return;
    if (this.rangeSelection() && this.selectionMode() === 'day') {
      this.handleRangeSelect(day.date, event?.shiftKey ?? false, 'day', this.isTouchPointer(event));
      return;
    }
    if (this.isMobile() && !this.isInline()) {
      this.pendingDate.set(day.date);
    } else {
      this.commitDate(day.date);
      if (!this.isInline()) this.close();
    }
  }

  /** Apply the pending date (mobile only) */
  protected applyMobile(): void {
    if (this.rangeSelection() && this.pendingRange()) {
      const staged = this.pendingRange();
      const anchor = this.pendingAnchor();
      if (staged && anchor) {
        const anchorIso = this.toIsoDate(anchor);
        this.rangeAnchor.set(anchor);
        this.range.set(staged);
        this.value.set(anchorIso);
        this.onChange(anchorIso);
        this.inputText.set(this.formatRangeForInput(staged));
        this.inputError.set(false);
        if (this.selectionMode() === 'week' && this.isFreshSingleSelection(staged, anchor, 'week')) this.weekSelected.emit(staged.start);
      }
      this.pendingDate.set(null);
      this.pendingWeekIso.set(null);
      this.pendingAnchor.set(null);
      this.pendingRange.set(null);
      this.close();
      return;
    }
    const pendingWeek = this.pendingWeekIso();
    if (pendingWeek) {
      // Week mode: emit the ISO week-start string the same way selectWeekRow does on desktop
      this.value.set(pendingWeek);
      this.onChange(pendingWeek);
      this.weekSelected.emit(pendingWeek);
    } else {
      const pending = this.pendingDate();
      if (pending) {
        this.commitDate(pending);
      }
    }
    this.pendingDate.set(null);
    this.pendingWeekIso.set(null);
    this.pendingAnchor.set(null);
    this.pendingRange.set(null);
    this.close();
  }

  /** Close without applying (mobile cancel) */
  protected closeMobile(): void {
    this.pendingDate.set(null);
    this.pendingWeekIso.set(null);
    this.pendingAnchor.set(null);
    this.pendingRange.set(null);
    this.close();
  }

  protected selectMonth(option: OptionCell, event?: MouseEvent | PointerEvent): void {
    if (option.isDisabled) return;
    const newCursor = setMonth(this.cursorDate(), option.value);
    this.cursorDate.set(newCursor);
    if (this.selectionMode() === 'month') {
      const firstOfMonth = startOfMonth(newCursor);
      if (this.rangeSelection()) {
        this.handleRangeSelect(firstOfMonth, event?.shiftKey ?? false, 'month', this.isTouchPointer(event));
        return;
      }
      if (this.isMobile() && !this.isInline()) {
        this.pendingDate.set(firstOfMonth);
        this.pendingWeekIso.set(null);
      } else {
        this.commitDate(firstOfMonth);
        if (!this.isInline()) this.close();
      }
    } else {
      this.viewMode.set('day');
    }
  }

  /** Select a whole calendar quarter (used when selectionMode='quarter'). */
  protected selectQuarter(option: OptionCell, event?: MouseEvent | PointerEvent): void {
    if (option.isDisabled) return;
    const year = getYear(this.cursorDate());
    const startMonth = option.value * 3;
    const firstOfQuarter = startOfMonth(new Date(year, startMonth, 1));
    if (this.rangeSelection() && this.selectionMode() === 'quarter') {
      this.handleRangeSelect(firstOfQuarter, event?.shiftKey ?? false, 'quarter', this.isTouchPointer(event));
      return;
    }
    if (this.isMobile() && !this.isInline()) {
      this.pendingDate.set(firstOfQuarter);
      this.pendingWeekIso.set(null);
    } else {
      this.commitDate(firstOfQuarter);
      if (!this.isInline()) this.close();
    }
  }

  /** Select a whole ISO week row (used when selectionMode='week'). */
  protected selectWeekRow(row: WeekRow, event?: MouseEvent | PointerEvent): void {
    if (this.rangeSelection() && this.selectionMode() === 'week') {
      this.handleRangeSelect(row.weekStart, event?.shiftKey ?? false, 'week', this.isTouchPointer(event));
      return;
    }
    const iso = this.toIsoDate(row.weekStart);
    if (this.isMobile() && !this.isInline()) {
      this.pendingDate.set(row.weekStart);
      this.pendingWeekIso.set(iso);
    } else {
      this.value.set(iso);
      this.onChange(iso);
      this.weekSelected.emit(iso);
      this.cursorDate.set(row.weekStart);
      if (!this.isInline()) this.close();
    }
  }

  protected selectYear(option: OptionCell, event?: MouseEvent | PointerEvent): void {
    if (option.isDisabled) return;
    const newCursor = setYear(this.cursorDate(), option.value);
    this.cursorDate.set(newCursor);
    if (this.selectionMode() === 'year') {
      const firstOfYear = new Date(option.value, 0, 1);
      if (this.rangeSelection()) {
        this.handleRangeSelect(firstOfYear, event?.shiftKey ?? false, 'year', this.isTouchPointer(event));
        return;
      }
      if (this.isMobile() && !this.isInline()) {
        this.pendingDate.set(firstOfYear);
        this.pendingWeekIso.set(null);
      } else {
        this.commitDate(firstOfYear);
        if (!this.isInline()) this.close();
      }
      return;
    }
    this.viewMode.set('month');
  }

  protected selectDecade(option: OptionCell): void {
    if (option.isDisabled) return;
    this.cursorDate.set(setYear(this.cursorDate(), option.value));
    this.viewMode.set('year');
  }

  /** Normalize a date to the start of its selection unit (week start / month start / quarter start / year start). */
  private normalizeToUnit(date: Date, mode: DatePickerSelectionMode): Date {
    if (mode === 'week') return startOfWeek(date, { weekStartsOn: this.weekStartsOn() });
    if (mode === 'month') return startOfMonth(date);
    if (mode === 'quarter') return startOfMonth(new Date(getYear(date), Math.floor(getMonth(date) / 3) * 3, 1));
    if (mode === 'year') return new Date(getYear(date), 0, 1);
    return date;
  }

  /** Normalize a date to the end of its selection unit (week end / month end / quarter end / year end). */
  private rangeEndForUnit(date: Date, mode: DatePickerSelectionMode): Date {
    if (mode === 'week') return endOfWeek(date, { weekStartsOn: this.weekStartsOn() });
    if (mode === 'month') return endOfMonth(date);
    if (mode === 'quarter') return endOfMonth(new Date(getYear(date), Math.floor(getMonth(date) / 3) * 3 + 2, 1));
    if (mode === 'year') return new Date(getYear(date), 11, 31);
    return date;
  }

  /** Expand an anchor..clicked pair to whole selection units (day exact, week Mon..Sun, month 1st..last, quarter/year whole period). */
  private normalizeRangeSpan(a: Date, b: Date, mode: DatePickerSelectionMode): DatePickerRange {
    const lo = a <= b ? a : b;
    const hi = a <= b ? b : a;
    return { start: this.toIsoDate(this.normalizeToUnit(lo, mode)), end: this.toIsoDate(this.rangeEndForUnit(hi, mode)) };
  }

  /** True when the committed range is still a fresh single selection around the anchor (exact day or one whole period). */
  private isFreshSingleSelection(current: DatePickerRange, anchor: Date, unit: DatePickerSelectionMode): boolean {
    const expected = this.normalizeRangeSpan(anchor, anchor, unit);
    if (current.start === expected.start && current.end === expected.end) return true;
    return current.start === current.end && current.start === this.toIsoDate(anchor);
  }

  /** True only for touch pointer events; mouse/pen/undefined clicks always re-anchor. */
  private isTouchPointer(event?: MouseEvent | PointerEvent | null): boolean {
    return (event as PointerEvent | undefined)?.pointerType === 'touch';
  }

  /** Range state machine shared by pointer + keyboard + today-button entry points. */
  private handleRangeSelect(date: Date, extend: boolean, unit: DatePickerSelectionMode, isTouch = false): void {
    if (this.isMobile() && !this.isInline()) {
      this.handleMobileRangeSelect(date, extend, unit);
      return;
    }
    const anchor = this.rangeAnchor();
    const current = this.range();
    const iso = this.toIsoDate(date);
    const completesSinglePoint =
      isTouch &&
      !extend &&
      anchor &&
      current &&
      this.isFreshSingleSelection(current, anchor, unit) &&
      (iso < current.start || iso > current.end);
    if (extend || completesSinglePoint) {
      const base = anchor ?? this.rangeStartDate() ?? date;
      if (!anchor) this.rangeAnchor.set(base);
      const next = this.normalizeRangeSpan(base, date, unit);
      this.range.set(next);
      this.cursorDate.set(date);
      this.inputText.set(this.formatRangeForInput(next));
      this.inputError.set(false);
      return;
    }
    this.rangeAnchor.set(date);
    const single = this.normalizeRangeSpan(date, date, unit);
    this.range.set(single);
    this.value.set(iso);
    this.onChange(iso);
    this.cursorDate.set(date);
    this.inputText.set(this.formatRangeForInput(single));
    this.inputError.set(false);
    if (unit === 'week') this.weekSelected.emit(iso);
  }

  /** Mobile staging variant — same state machine on pending signals, committed by Apply. */
  private handleMobileRangeSelect(date: Date, extend: boolean, unit: DatePickerSelectionMode = this.selectionMode()): void {
    const anchor = this.pendingAnchor() ?? this.rangeAnchor();
    const current = this.pendingRange() ?? this.range();
    const iso = this.toIsoDate(date);
    const completesSinglePoint =
      !extend && anchor && current && this.isFreshSingleSelection(current, anchor, unit) && (iso < current.start || iso > current.end);
    if ((extend || completesSinglePoint) && anchor) {
      this.pendingAnchor.set(anchor);
      this.pendingRange.set(this.normalizeRangeSpan(anchor, date, unit));
      this.cursorDate.set(date);
      return;
    }
    this.pendingAnchor.set(date);
    this.pendingRange.set(this.normalizeRangeSpan(date, date, unit));
    this.pendingDate.set(date);
    this.cursorDate.set(date);
  }

  /** Shift+Arrow extension for day cells. Moves focus to the target cell after extending. */
  protected onDayCellKeyDown(event: KeyboardEvent, cell: DayCell): void {
    if (!this.rangeSelection() || !event.shiftKey) return;
    const delta = this.arrowDelta(event.key, 1, 7);
    if (delta === null) return;
    event.preventDefault();
    const target = new Date(cell.date);
    target.setDate(target.getDate() + delta);
    if (!this.isDateAllowed(target)) return;
    if (!isSameMonth(target, this.cursorDate())) this.cursorDate.set(target);
    this.handleRangeSelect(target, true, 'day');
    const iso = this.toIsoDate(target);
    setTimeout(() => {
      const el = this.hostRef.nativeElement.querySelector(`[data-day-iso="${iso}"]`) as HTMLElement | null;
      el?.focus();
    }, 0);
  }

  /** Shift+Arrow extension for week rows (all four arrows move one week). */
  protected onWeekRowKeyDown(event: KeyboardEvent, row: WeekRow): void {
    if (!this.rangeSelection() || !event.shiftKey) return;
    const delta = this.arrowDelta(event.key, 7, 7);
    if (delta === null) return;
    event.preventDefault();
    const target = new Date(row.weekStart);
    target.setDate(target.getDate() + delta);
    const weekStart = startOfWeek(target, { weekStartsOn: this.weekStartsOn() });
    if (!isSameMonth(weekStart, this.cursorDate())) this.cursorDate.set(weekStart);
    this.handleRangeSelect(weekStart, true, 'week');
    const iso = this.toIsoDate(weekStart);
    setTimeout(() => {
      const el = this.hostRef.nativeElement.querySelector(`[data-week-iso="${iso}"]`) as HTMLElement | null;
      el?.focus();
    }, 0);
  }

  /** Shift+Arrow extension for month cells (left/right ±1, up/down ±3). */
  protected onMonthCellKeyDown(event: KeyboardEvent, cell: OptionCell): void {
    if (!this.rangeSelection() || !event.shiftKey) return;
    const delta = this.arrowDelta(event.key, 1, 3);
    if (delta === null) return;
    event.preventDefault();
    const cursorYear = getYear(this.cursorDate());
    const absolute = cursorYear * 12 + cell.value + delta;
    const targetYear = Math.floor(absolute / 12);
    const targetMonth = ((absolute % 12) + 12) % 12;
    const target = startOfMonth(new Date(targetYear, targetMonth, 1));
    if (!this.isRangeSelectable(target, endOfMonth(target))) return;
    if (targetYear !== cursorYear) this.cursorDate.set(target);
    this.handleRangeSelect(target, true, 'month');
    const iso = this.toIsoDate(target);
    setTimeout(() => {
      const el = this.hostRef.nativeElement.querySelector(`[data-option-iso="${iso}"]`) as HTMLElement | null;
      el?.focus();
    }, 0);
  }

  /** Shift+Arrow extension for quarter cells (left/right ±1, up/down ±2). */
  protected onQuarterCellKeyDown(event: KeyboardEvent, cell: OptionCell): void {
    if (!this.rangeSelection() || !event.shiftKey) return;
    const delta = this.arrowDelta(event.key, 1, 2);
    if (delta === null) return;
    event.preventDefault();
    const cursorYear = getYear(this.cursorDate());
    const absolute = cursorYear * 4 + cell.value + delta;
    const targetYear = Math.floor(absolute / 4);
    const targetQuarter = ((absolute % 4) + 4) % 4;
    const target = startOfMonth(new Date(targetYear, targetQuarter * 3, 1));
    const targetEnd = endOfMonth(new Date(targetYear, targetQuarter * 3 + 2, 1));
    if (!this.isRangeSelectable(target, targetEnd)) return;
    if (targetYear !== cursorYear) this.cursorDate.set(target);
    this.handleRangeSelect(target, true, 'quarter');
    this.focusOptionIso(target);
  }

  /** Shift+Arrow extension for year cells (left/right ±1, up/down ±3). */
  protected onYearCellKeyDown(event: KeyboardEvent, cell: OptionCell): void {
    if (!this.rangeSelection() || !event.shiftKey) return;
    const delta = this.arrowDelta(event.key, 1, 3);
    if (delta === null) return;
    event.preventDefault();
    const target = new Date(cell.value + delta, 0, 1);
    if (!this.isRangeSelectable(target, new Date(target.getFullYear(), 11, 31))) return;
    this.handleRangeSelect(target, true, 'year');
    this.focusOptionIso(target);
  }

  private focusOptionIso(target: Date): void {
    const iso = this.toIsoDate(target);
    setTimeout(() => {
      const el = this.hostRef.nativeElement.querySelector(`[data-option-iso="${iso}"]`) as HTMLElement | null;
      el?.focus();
    }, 0);
  }

  private arrowDelta(key: string, horizontal: number, vertical: number): number | null {
    if (key === 'ArrowLeft') return -horizontal;
    if (key === 'ArrowRight') return horizontal;
    if (key === 'ArrowUp') return -vertical;
    if (key === 'ArrowDown') return vertical;
    return null;
  }

  private commitTypedInput(): void {
    const text = this.inputText().trim();

    if (!text) {
      this.handleEmptyInput();
      return;
    }

    const parsed = parseTypedDate(text);
    if (!parsed) {
      this.inputError.set(true);
      return;
    }

    if (!this.isDateAllowed(parsed)) {
      this.inputError.set(true);
      return;
    }

    if (this.rangeSelection()) {
      this.handleRangeSelect(this.normalizeToUnit(parsed, this.selectionMode()), false, this.selectionMode());
      return;
    }
    this.commitDate(parsed);
    if (!this.isInline()) this.close();
  }

  private handleEmptyInput(): void {
    if (this.required()) {
      this.inputError.set(true);
    } else {
      this.value.set('');
      this.onChange('');
      this.rangeAnchor.set(null);
      this.range.set(null);
      this.inputError.set(false);
    }
  }

  private commitDate(date: Date): void {
    const iso = this.toIsoDate(date);
    this.value.set(iso);
    this.onChange(iso);
    this.cursorDate.set(date);
    this.inputText.set(this.formatDateForInput(date));
    this.inputError.set(false);
  }

  private parseIsoDate(value: string | null | undefined): Date | null {
    if (!value) return null;
    const parts = value.split('-').map(Number);
    if (parts.length !== 3) return null;
    const [year, month, day] = parts;
    if (!year || !month || !day) return null;
    const date = new Date(year, month - 1, day);
    if (getYear(date) !== year || getMonth(date) !== month - 1 || date.getDate() !== day) return null;
    return date;
  }

  private toIsoDate(date: Date): string {
    const year = String(getYear(date));
    const month = String(getMonth(date) + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private getYearGridStart(year: number): number {
    return Math.floor(year / 10) * 10 - 1;
  }

  private getDecadeGridStart(year: number): number {
    return Math.floor(year / 100) * 100 - 10;
  }

  private isDateAllowed(date: Date): boolean {
    const min = this.minDate();
    const max = this.maxDate();
    if (min !== null && date < min) return false;
    if (max !== null && date > max) return false;
    if (this.isDateExplicitlyDisabled(date)) return false;
    return true;
  }

  private isRangeSelectable(start: Date, end: Date): boolean {
    const min = this.minDate();
    const max = this.maxDate();
    if (min !== null && end < min) return false;
    if (max !== null && start > max) return false;
    const effectiveStart = min !== null && start < min ? min : start;
    const effectiveEnd = max !== null && end > max ? max : end;
    if (effectiveStart > effectiveEnd) return false;
    return eachDayOfInterval({ start: effectiveStart, end: effectiveEnd }).some(day => !this.isDateExplicitlyDisabled(day));
  }

  private isDateExplicitlyDisabled(date: Date): boolean {
    const isoDate = this.toIsoDate(date);
    if (this.disabledDateSet().has(isoDate)) return true;
    const matcher = this.disabledDateMatcher();
    return matcher?.(isoDate, date) ?? false;
  }

  private startPositionMonitoring(): void {
    const check = () => {
      if (!this.open()) return;
      const overlayRef = this.connectedOverlay()?.overlayRef;
      const wrapper = this.hostRef.nativeElement.querySelector('.zag-date-picker__input-wrapper') as HTMLElement | null;
      if (overlayRef && wrapper) {
        const w = Math.round(wrapper.getBoundingClientRect().width);
        if (this.lastOverlayWidth !== w) {
          overlayRef.updateSize({ minWidth: w });
          this.lastOverlayWidth = w;
        }
        overlayRef.updatePosition();
      }
      this.rafId = requestAnimationFrame(check);
    };
    this.rafId = requestAnimationFrame(check);
  }

  private stopPositionMonitoring(): void {
    if (this.rafId !== undefined) {
      cancelAnimationFrame(this.rafId);
      this.rafId = undefined;
    }
    this.lastOverlayWidth = undefined;
  }
}
